<script setup lang="ts">
import type Server from '@/core/resources/Server'
import GenericTable from '@/components/GenericTable.vue'
import ServerTableLine from '@/components/tables/ServerTableLine.vue'
import { computed, onMounted, ref, watch } from 'vue'
import { serverService } from '@/core/services'
import { useStore } from '@/core/store'
import { useRouter } from 'vue-router'
import { handleError } from '@/utils/ErrorHandler'
import { flashMessage } from '@/utils/FlashMessages'
import { popupUtil } from '@/utils/Popup'

const router = useRouter()
const fetchServers = () => {
  serverService
    .getAll(store.getAccessToken())
    .then((server) => {
      serverList.value = server
      selectedMap.value = {}
      server.forEach((n) => {
        selectedMap.value[n.id] = false
      })
    })
    .catch(handleError)
}

const deleteServers = async () => {
  const confirmed = await popupUtil.confirm('Are you sure you want to delete the selected servers?')
  if (!confirmed) return

  serverService
    .deleteMany(selectedServers(), store.getAccessToken())
    .then(() => {
      flashMessage.success('Servers deleted successfully')
      fetchServers()
    })
    .catch(handleError)
}

const deleteServer = async (server: Server) => {
  const confirmed = await popupUtil.confirm('Are you sure you want to delete this server?')
  if (!confirmed) return

  serverService
    .delete(server, store.getAccessToken())
    .then(() => {
      flashMessage.success('Server deleted successfully')
      fetchServers()
    })
    .catch(handleError)
}

const selectedServers = () => {
  return serverList.value.filter((s) => selectedMap.value[s.id])
}

const sortedServerList = computed(() => {
  let copy: Server[] = []
  if (search.value)
    copy = serverList.value.filter((server) => {
      return server.name.toLowerCase().includes(search.value.toLowerCase())
    })
  else copy = serverList.value.slice()

  copy.sort((a, b) => {
    switch (sortParams.value.key) {
      case 'name':
        return a.name.localeCompare(b.name)
      case 'hostname':
        return a.hostname.localeCompare(b.hostname)
      case 'username':
        return a.username.localeCompare(b.username)
      default:
        return 0
    }
  })

  return sortParams.value.desc ? copy.reverse() : copy
})

const allSelected = ref(false)
const store = useStore()
const serverList = ref<Server[]>([])
const selectedMap = ref<{ [key: string]: boolean }>({})
const search = ref('')
const sortParams = ref({ key: '', desc: false })
const headers = [
  { key: 'name', label: 'Server name' },
  { key: 'hostname', label: 'Hostname' },
  { key: 'username', label: 'Username' },
  { key: 'action', label: 'Action' }
]

onMounted(() => {
  fetchServers()
})

watch(allSelected, (value) => {
  for (const key in selectedMap.value) {
    selectedMap.value[key] = value
  }
})
</script>

<template>
  <div class="container">
    <h1>List of servers</h1>
    <div class="toolbar">
      <router-link to="/servers/new" class="button" id="create-server">
        <span class="material-symbols-outlined">add</span>
        <span>Create Server</span>
      </router-link>

      <button :disabled="selectedServers().length === 0" @click="deleteServers" id="delete-servers">
        <span class="material-symbols-outlined">delete</span>
        <span>Delete</span>
      </button>

      <div class="search-bar">
        <span class="material-symbols-outlined search-icon">search</span>
        <input type="text" v-model="search" placeholder="Search..." />
      </div>
    </div>

    <GenericTable :headers="headers" v-model:sortParams="sortParams" v-model:checked="allSelected" @fetch="fetchServers">
      <ServerTableLine
        v-for="server in sortedServerList"
        :key="server.id"
        :server="server"
        v-model="selectedMap[server.id]"
        @delete="deleteServer(server)"
        @edit="router.push({ name: 'edit-server', params: { id: server.id } })" />
    </GenericTable>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/styles/table.scss';
</style>
